import React, { useState, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Toast } from 'antd-mobile';
import MultiPhotoGalleryView from '../../components/MultiPhotoGalleryView';
import { pdaOpenCamera, startPrepareCameraLocation, FirstPhotoWatermarkLocation } from '../../../../utils/appRuntime';
import { getCurrentServerTimeMillis } from '../../../../utils/pdaCameraUtils';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../../../graphql/queries/warehouseQueries';

const MIN_PHOTO_COUNT = 1;
const MAX_PHOTO_COUNT = 3;

export interface UnsealingInteriorPhotoFormProps {
  photos?: string[];
  onPhotosChange?: (photos: string[]) => void;
  onFirstPhotoLocationChange?: (loc: FirstPhotoWatermarkLocation | null) => void;
}

/**
 * 解封车-车内照片上传表单（最少1张，最多3张）
 * 交互与发车上传封签照一致：连拍、删除、画廊展示
 */
export const UnsealingInteriorPhotoForm: React.FC<UnsealingInteriorPhotoFormProps> = ({
  photos = [],
  onPhotosChange,
  onFirstPhotoLocationChange,
}) => {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE);
  const [photosArray, setPhotosArray] = useState<string[]>(photos);

  useEffect(() => {
    setPhotosArray(photos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 仅挂载时同步外部初始值

  useEffect(() => {
    startPrepareCameraLocation();
  }, []);

  const handleBurstCapture = async () => {
    const currentCount = photosArray.length;
    const remainingCount = Math.max(0, MAX_PHOTO_COUNT - currentCount);
    const minSelectionCount = currentCount >= MIN_PHOTO_COUNT ? 0 : MIN_PHOTO_COUNT - currentCount;

    const photoTitles = [
      t('pda.pages.tms.unsealing.firstPhoto') || t('pda.pages.exception.firstPhoto') || '第一张照片',
      t('pda.pages.tms.unsealing.secondPhoto') || t('pda.pages.exception.secondPhoto') || '第二张照片',
      t('pda.pages.tms.unsealing.thirdPhoto') || t('pda.pages.exception.thirdPhoto') || '第三张照片',
    ];
    const serverTimeMillis = await getCurrentServerTimeMillis(apolloClient);

    await pdaOpenCamera(
      remainingCount,
      minSelectionCount,
      t('pda.pages.tms.unsealing.photoTips') || '',
      photoTitles,
      t('pda.pages.tms.unsealing.complete') || t('pda.pages.exception.complete') || '',
      async (newPhotos: string[], loc?: FirstPhotoWatermarkLocation | null) => {
        try {
          if (newPhotos && newPhotos.length > 0) {
            setPhotosArray((prev) => {
              const updated = [...prev, ...newPhotos].slice(0, MAX_PHOTO_COUNT);
              onPhotosChange?.(updated);
              return updated;
            });
          }
          if (loc != null) {
            onFirstPhotoLocationChange?.(loc);
          }
        } catch (error) {
          console.error('解封车车内照片处理失败:', error);
          Toast.show({
            content: t('pda.pages.exception.multiPhotoProcessingFailed') || 'Photo processing failed',
            position: 'center',
          });
        }
      },
      {
        enableWatermark: true,
        ianaTimeZone: warehouseData?.warehouse?.timezoneLocation,
        serverTimeMillis
      }
    );
  };

  const handleDelete = (index: number) => {
    const updated = photosArray.filter((_, i) => i !== index);
    setPhotosArray(updated);
    onPhotosChange?.(updated);
  };

  return (
    <div className="space-y-2">
      <div className="text-base font-medium text-gray-700">
        {t('pda.pages.unsealing.uploadInteriorPhotos')}
      </div>
      <MultiPhotoGalleryView
        photos={photosArray}
        onDelete={handleDelete}
        onNativeBurstCapture={handleBurstCapture}
        maxDisplayCount={MAX_PHOTO_COUNT}
      />
    </div>
  );
};
